import { FoodsTextInput, HelperBox, WhiteBox } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { useUpsertActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useUpsertActionPlanAction";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

type EmptyActionInfoProps = {
    actionPlanAction: ActionPlanAction;
};
export const EmptyActionInfo = (props: EmptyActionInfoProps) => {
    const { actionPlanAction } = props;

    const theme = useTheme();

    // This is an arbitrary limit defined in the design system
    const characterLimit = 30;
    const { t } = useTranslation();
    const { actionPlanMetadata } = useActionsContext();
    const upsertAPA = useUpsertActionPlanAction({ actionPlanID: actionPlanMetadata.actionPlanID });

    const delayedTitle = useDelayedSearch({
        startSearch: actionPlanAction.userMessageTitle,
        actionOnDelay: (search) => {
            upsertAPA.mutate({
                id: actionPlanAction.id,
                userMessageTitle: search,
            });
        },
    });
    const delayedDetail = useDelayedSearch({
        startSearch: actionPlanAction.userMessageBody,
        actionOnDelay: (search) => {
            upsertAPA.mutate({
                id: actionPlanAction.id,
                userMessageBody: search,
            });
        },
    });

    return (
        <WhiteBox>
            <Stack gap={theme.spacing(4)} padding={theme.spacing(4)}>
                <Stack>
                    <HelperBox>
                        <Typography variant="small-medium">{t("action-plan.empty-action.info")}</Typography>
                    </HelperBox>
                </Stack>
                <Stack gap={theme.spacing(1)}>
                    <Stack flexDirection={"row"} gap={theme.spacing(0.5)}>
                        <Typography variant="h5">{t("action-plan.empty-action.description")}</Typography>
                        <Typography variant="body-medium">
                            {t("action-plan.empty-action.max-length-description", { value: characterLimit })}
                        </Typography>
                        <Typography variant="h5">{":"}</Typography>
                    </Stack>
                    <FoodsTextInput
                        value={delayedTitle.search}
                        onChange={(value) => {
                            if (value.length > characterLimit) {
                                return;
                            }
                            delayedTitle.setSearch(value);
                        }}
                        ExtraInputProps={{
                            // It approximately looks like the max character of 30
                            sx: { maxWidth: "30em" },
                        }}
                    />
                </Stack>
                <Stack gap={theme.spacing(1)}>
                    <Stack flexDirection={"row"} gap={theme.spacing(0.5)}>
                        <Typography variant="h5">{t("action-plan.empty-action.more-details")}</Typography>
                        <Typography variant="h5">{":"}</Typography>
                    </Stack>
                    <FoodsTextInput
                        value={delayedDetail.search}
                        onChange={(value) => {
                            if (value.length > characterLimit) {
                                return;
                            }
                            delayedDetail.setSearch(value);
                        }}
                    />
                </Stack>
            </Stack>
        </WhiteBox>
    );
};
